import React from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useAuth } from '../../../contexts/AuthContext';
import { Unauthorized } from '../../../components/Unauthorized';
import { Chart, PatientDoc } from '@usacsmideo/types';
import useChart from '../../../hooks/useChart';
// import { Header } from '../../../../components/header/Header';
import { PasswordUpdateForm } from '../../../components/forms/PasswordUpdate';
import Toggle from '../../../components/forms/Toggle';
import usePatientDoc from '../../../hooks/usePatientDoc';
import { useWillLoseAccessOnAal2Drop } from '../../../hooks/useWillLoseAccessOnAal2Drop';
import { useUpdateUserDoc } from '../../../hooks/useUpdateUserDoc';
import { Button } from '../../../components';
import * as Sentry from '@sentry/browser';
import PaymentPrefs from '../../../components/forms/PaymentPrefs';
import { EmergencyCardSection } from '../../../components/EmergencyCardSection';
import { SmsEnrollmentSection } from '../../../components/mfa/SmsEnrollmentSection';
import { PasskeyEnrollmentSection } from '../../../components/mfa/PasskeyEnrollmentSection';
import { usePasskeyEnrollment } from '../../../hooks/usePasskeyEnrollment';
import { multiFactor } from 'firebase/auth';

export const Route = createFileRoute('/_authenticated/$patientId/profile')({
  component: ProfilePage,
});

function ProfilePage() {
  const { patientId } = Route.useParams();
  const { currentUser, firebaseSignOut } = useAuth();
  const chartQuery = useChart(patientId);
  const data = chartQuery.data as Chart;
  const patientDocQuery = usePatientDoc(patientId);
  const patientInfo = patientDocQuery.data as PatientDoc;
  const { mutate: mutatePatientDoc } = useUpdateUserDoc();

  // CU-86akkg3gu Part 1 — factor state for the password-change warning.
  // ⚠️ `credentials === null` is UNKNOWN (loading or failed read), not "none".
  const { credentials: passkeyCredentials, refreshCredentials } = usePasskeyEnrollment();
  React.useEffect(() => {
    void refreshCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ⚠️ READ THE SMS FACTOR LIVE, NOT FROM `mfa.enrollmentState`.
   *
   * `enrollmentState` is a MIRROR: `MfaEnrollmentState.d.ts` states it is
   * "DERIVED BY POLLING, AND LAGS BY UP TO ONE SWEEP INTERVAL" (five minutes),
   * and that "anything needing factor truth RIGHT NOW must read Identity
   * Platform directly."
   *
   * This page hosts `SmsEnrollmentSection` directly above the password form, so
   * a patient can enrol SMS and reach the form inside that lag. The mirror would
   * still read 'none', the warning would withhold "your text-message codes will
   * keep working", and the patient would be told their passkeys die with no
   * indication they retain a working factor — the reassurance suppressed exactly
   * when it is true.
   *
   * `multiFactor(user).enrolledFactors` is the same SDK surface `useSmsEnrollment`
   * enrols through, so this is current truth rather than a derived copy.
   *
   * ⚠️ `null` STAYS UNKNOWN. No signed-in user means we did not read it, which
   * must not render as "no SMS".
   *
   * Found by adversarial review, 2026-09-23.
   */
  /**
   * ⚠️ CANONICAL RULE, NOT A LOCAL COPY. See the hook's header — an earlier
   * version re-derived `hasValidException` inline and disagreed with it on two
   * input shapes and on the clock. Adversarial review round 3.
   */
  const willLoseAccessOnPasswordChange = useWillLoseAccessOnAal2Drop(patientInfo);

  const hasSmsFactor = React.useMemo<boolean | null>(() => {
    if (!currentUser) return null;
    try {
      return multiFactor(currentUser).enrolledFactors.length > 0;
    } catch {
      // A read we could not make is UNKNOWN, never "none".
      return null;
    }
  }, [currentUser]);

  const [isConcierge, setIsConcierge] = React.useState(false);

  const [hasDoctor, setHasDoctor] = React.useState(false);
  const [hasHospital, setHasHospital] = React.useState(false);
  const [hasAttorney, setHasAttorney] = React.useState(false);
  const [okToText, setOkToText] = React.useState(false);
  const [needsTextingUpdate, setNeedsTextingUpdate] = React.useState(false);
  const [needsMailingPrefsUpdate, setNeedsMailingPrefsUpdate] = React.useState(false);

  // ⚠️ CU-17tm466h657 — A PATIENT WHOSE PAYMENT IS RECORDED (CARD, AOB OR A $0 DISCOUNT) CANNOT CHANGE
  // PATIENT TYPE HERE: THE TOGGLE IS REPLACED BY A NOTICE, AND THIS WRITER REFUSES AS A BACKSTOP.
  const isPaid = data?.PAYMENT_INFO?.alreadyPaid === true;

  const writeNavTypeBase = (newBase: 'self' | 'concierge') => {
    if (isPaid) return;
    mutatePatientDoc(
      {
        patientId: patientId,
        navType: {
          ...patientInfo?.navType,
          base: newBase,
        },
      },
      {
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating patient type: ${error.message}`);
          console.error(error);
        },
      },
    );
  };

  const handleTextingUpdate = () => {
    mutatePatientDoc(
      {
        patientId: patientId,
        data: {
          canText: okToText,
        },
      },
      {
        onSuccess: () => {
          // Alert.alert("Successfully updated texting preferences");
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  const handleMailingPrefsUpdate = () => {
    mutatePatientDoc(
      {
        patientId: patientId,
        navInfo: {
          hasDoctor: hasDoctor,
          hasHospital: hasHospital,
          hasAttorney: hasAttorney,
          uploadingDocuments: patientInfo?.navInfo?.uploadingDocuments ?? true,
        },
      },
      {
        onSuccess: () => {
          // Alert.alert("Successfully updated mailing preferences");
        },
        onError: (error) => {
          Sentry.captureException(error);
          alert(`Error updating document: ${error.message}`); // TELL THE USER
          console.error(error); // GET THE STACKTRACE
        },
      },
    );
  };

  // KEEP LOCAL IN LINE WITH FIRESTORE ON LOAD & UPDATE
  React.useEffect(() => {
    if (patientInfo) {
      // ?. : A PATIENT DOC CAN HAVE NO navType (13 OF 272 ON STAGING, 2026-10-16); THIS CRASHED THE PAGE (MIDEO-UNIFIED-DB)
      setIsConcierge(patientInfo?.navType?.base === 'concierge');
      setHasDoctor(patientInfo.navInfo?.hasDoctor ?? true);
      setHasHospital(patientInfo.navInfo?.hasHospital ?? true);
      setHasAttorney(patientInfo.navInfo?.hasAttorney ?? true);
      setOkToText(patientInfo.canText ?? false);
    }
  }, [patientInfo]);

  // WATCH FOR TEXTING PREFS CHANGES
  React.useEffect(() => {
    let prefInFirestore = patientInfo?.canText;
    if (prefInFirestore == null) prefInFirestore = false;
    const localPref = okToText;
    setNeedsTextingUpdate(prefInFirestore !== localPref);
  }, [okToText, patientInfo]);

  // WATCH FOR DOCTOR/HOSPITAL/ATTORNEY PREFS CHANGES
  React.useEffect(() => {
    const navInfo = patientInfo?.navInfo;
    const firestoreDoctor = navInfo?.hasDoctor ?? true;
    const firestoreHospital = navInfo?.hasHospital ?? true;
    const firestoreAttorney = navInfo?.hasAttorney ?? true;
    const differs =
      !navInfo ||
      firestoreDoctor !== hasDoctor ||
      firestoreHospital !== hasHospital ||
      firestoreAttorney !== hasAttorney;
    setNeedsMailingPrefsUpdate(differs);
  }, [patientInfo, hasDoctor, hasHospital, hasAttorney]);

  if (chartQuery.isLoading || !data) {
    return <div>Loading patient information...</div>;
  }
  if (!currentUser) {
    return <div>Loading current user</div>;
  }
  if (currentUser?.uid !== patientId) {
    return <Unauthorized />;
  }
  if (currentUser?.uid === patientId && data?.PATIENT_INFO_1) {
    return (
      <>
        <div id="account-info-page" className="flex w-screen flex-col items-center justify-center">
          <h1 id="account-info-page-header" className="text-mideo-darkred text-4xl font-bold">
            Account Information
          </h1>
          <div>
            <p className="text-mideo-darkred mt-6 text-center text-xl font-bold">Patient Type</p>
            <div>
              {isPaid ? (
                <p id="patient-type-locked" className="my-4 max-w-md text-center text-lg">
                  Your patient type is {patientInfo?.navType?.base === 'concierge' ? 'CLARIFICATIONS' : 'FULL CODE'}.
                  To change it, contact support at{' '}
                  <a className="underline" href="mailto:info@mideohealth.com">
                    info@mideohealth.com
                  </a>
                  .
                </p>
              ) : (
                <Toggle
                  name="patient-type"
                  label="Patient Type Toggle"
                  checked={isConcierge}
                  onChange={(checked) => {
                    setIsConcierge(checked);
                    writeNavTypeBase(checked ? 'concierge' : 'self');
                  }}
                  leftSideLabel="FULL CODE"
                  rightSideLabel="CLARIFICATIONS"
                />
              )}
            </div>
          </div>

          <PaymentPrefs />
          <p className="text-mideo-darkred mt-6 text-center text-4xl font-bold">Communication Preferences</p>
          <div className="items-left flex flex-col">
            <Toggle
              name="text-msg-preferences"
              label="Is it OK to text you?"
              checked={okToText}
              onChange={(checked) => {
                setOkToText(checked);
              }}
              rightSideLabel="I agree to receive informational and/or scheduling text messages.*"
            />
            <p className="ml-6 ml-12 text-sm italic">*You may opt-out at any time replying STOP.</p>
            {needsTextingUpdate && (
              <div className="flex flex-col items-center">
                <Button
                  submitted={false}
                  id="update-info-button"
                  textContent="Update Text Messaging Preferences"
                  type="button"
                  disabled={!needsTextingUpdate}
                  onClick={() => {
                    handleTextingUpdate();
                  }}
                />
              </div>
            )}
            <Toggle
              name="has-doctor"
              label="Would you like a copy of your MIDEO sent to your PCP?"
              checked={hasDoctor}
              onChange={(checked) => {
                setHasDoctor(checked);
              }}
              rightSideLabel="Forward my MIDEO to my PCP."
            />
            <Toggle
              name="has-doctor"
              label="Would you like a copy of your MIDEO sent to your hospital?"
              checked={hasHospital}
              onChange={(checked) => {
                setHasHospital(checked);
              }}
              rightSideLabel="Forward my MIDEO to my hospital."
            />
            <Toggle
              name="has-doctor"
              label="Would you like a copy of your MIDEO sent to your attorney?"
              checked={hasAttorney}
              onChange={(checked) => {
                setHasAttorney(checked);
              }}
              rightSideLabel="Forward my MIDEO to my attorney."
            />
          </div>
          {needsMailingPrefsUpdate && (
            <div className="flex flex-col items-center">
              <Button
                submitted={false}
                id="update-info-button"
                textContent="Update Mailing Preferences"
                type="button"
                disabled={!needsMailingPrefsUpdate}
                onClick={() => {
                  handleMailingPrefsUpdate();
                }}
              />
            </div>
          )}
          <EmergencyCardSection chart={data} />

          {/* SMS second-factor enrollment (CU-86ajwpnek). Optional here and
              pre-cutover; the SAME component is mounted by the MFA route gate,
              where it is mandatory. One implementation, two hosts — see
              SmsEnrollmentSection's header. */}
          <div className="w-5/6">
            <SmsEnrollmentSection variant="profile" />
          </div>

          {/* Passkey enrollment + per-credential registry (CU-86ajzr8x1).
              Sibling of the SMS section: both factors stay live, and neither is
              gated behind the other. ⚠️ Passkey SIGN-IN is not wired yet
              (CU-86ajzr94n) — enrollment ships first so a broken passkey login
              cannot lock a patient out. */}
          <div className="w-5/6">
            <PasskeyEnrollmentSection />
          </div>

          {/* ECN Sessions Surface (CU-86aj1c9ev): contextual entry to MIDEO Scan
              Activity, grouped with the emergency card it reports on. Peer to the
              top-level Header entry; presentation-only (no card/consent logic). */}
          <Link
            to="/sessions"
            className="text-mideo-darkred text-sm font-semibold underline hover:opacity-80"
          >
            View MIDEO Scan Activity →
          </Link>

          <div id="account-info-page-main" className="flex w-5/6 flex-row justify-around">
            {/*
              CU-86akkg3gu Part 1 — the warning copy is factor-aware because the
              FACTS differ: a password change reaps PASSKEYS and SMS SURVIVES
              (measured, CU-86akjurnv Addendum 6).

              ⚠️ `null` MEANS UNKNOWN, NEVER "NO". `passkeyCredentials === null`
              is the loading/failed-read state, mirroring the distinction
              `PasskeyEnrollmentSection` already makes (`credentials !== null &&
              !hasCredentials`). Reading it as "no passkey" would suppress a
              warning a passkey holder needs.
            */}
            <PasswordUpdateForm
              factors={{
                hasPasskey: passkeyCredentials === null ? null : passkeyCredentials.length > 0,
                hasSms: hasSmsFactor,
                mfaEnforced: willLoseAccessOnPasswordChange,
              }}
              /* ⚠️ Re-read on open: `PasskeyEnrollmentSection` sits directly
                 above this form with its OWN `usePasskeyEnrollment` instance,
                 and the two do not share state. A patient who enrols or removes
                 a passkey there would otherwise see a warning computed at page
                 load. Adversarial review, 2026-09-23. */
              onOpen={() => void refreshCredentials()}
              /* ⚠️ Signs out after a successful change, when enforced —
                 CU-86aknw0ku. `updatePassword` mints a token with no AAL2
                 claim, so an enforced patient's PHI reads are all denied. The
                 portal HID this: cached navigation kept working while the
                 session could not read Firestore, and only a refresh revealed
                 it by dropping them on the MFA gate. */
              willSignOut={willLoseAccessOnPasswordChange}
              onSignOut={() => {
                // ⚠️ WARN FIRST, THEN SIGN OUT. `window.alert` is SYNCHRONOUS and
                // BLOCKING on the web, so this warns and waits before the
                // sign-out routes away — the patient is never ejected without
                // explanation.
                //
                // ⚠️ Mobile needs an explicit callback to achieve the same thing
                // (`Alert.alert` is async), and briefly had the order reversed.
                // Do not "align" this to mobile's shape: the APIs differ, the
                // ORDER is what must match.
                alert('Your password has been updated. Please sign in again with your new password.');
                void firebaseSignOut();
              }}
            />
          </div>
        </div>
      </>
    );
  }

  return <div>Loading...</div>;
}
